<!-- src/lib/components/MyTasksView.svelte -->
<script lang="ts">
  import { pmStore } from '../stores/pm.svelte.ts';
  import type { Card } from '../types.ts';

  interface Props {
    onOpenCard: (id: string) => void;
  }
  let { onOpenCard }: Props = $props();

  const PRIORITY_ORDER = ['urgent', 'high', 'medium', 'low', 'none'];
  const PRIORITY_LABELS: Record<string, string> = {
    urgent: 'Urgent', high: 'High', medium: 'Medium', low: 'Low', none: 'No Priority'
  };
  const PRIORITY_COLORS: Record<string, string> = {
    urgent: 'oklch(0.62 0.26 25)', high: 'oklch(0.78 0.16 85)', medium: 'oklch(0.58 0.22 265)', low: 'rgba(255,255,255,0.3)', none: 'rgba(255,255,255,0.1)'
  };

  // Flat list of all cards from all loaded boards
  const allCards: Card[] = $derived(
    Object.values(pmStore.boardDetails)
      .flatMap(bd => bd.columns.flatMap(c => c.cards))
  );

  // Group by priority
  const grouped: Map<string, Card[]> = $derived((() => {
    const m = new Map<string, Card[]>();
    for (const p of PRIORITY_ORDER) m.set(p, []);
    for (const card of allCards) {
      const group = m.get(card.priority) ?? m.get('none')!;
      group.push(card);
    }
    return m;
  })());

  let collapsedGroups = $state(new Set<string>());

  function toggleGroup(p: string) {
    const next = new Set(collapsedGroups);
    if (next.has(p)) next.delete(p); else next.add(p);
    collapsedGroups = next;
  }

  function getColumnName(card: Card): string {
    if (!card.column_id) return '';
    for (const bd of Object.values(pmStore.boardDetails)) {
      for (const col of bd.columns) {
        if (col.id === card.column_id) return col.name;
      }
    }
    return '';
  }

  function getProjectName(card: Card): string {
    return pmStore.projects.find(p => p.id === card.project_id)?.name ?? '';
  }

  function formatDue(date: string): string {
    const d = new Date(date);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  function isDue(date: string): boolean {
    return new Date(date) < new Date();
  }
</script>

<div class="my-tasks">
  <header class="tasks-header">
    <h2>My Tasks</h2>
    <span class="total-badge">{allCards.length}</span>
  </header>

  {#if allCards.length === 0}
    <div class="empty-state">
      <p>No tasks yet. Create a project and add cards to get started.</p>
    </div>
  {:else}
    {#each PRIORITY_ORDER as priority}
      {@const cards = grouped.get(priority) ?? []}
      {#if cards.length > 0}
        <div class="priority-group">
          <button
            class="group-header"
            onclick={() => toggleGroup(priority)}
          >
            <span class="group-chevron" class:collapsed={collapsedGroups.has(priority)}>›</span>
            <span class="priority-dot" style="background: {PRIORITY_COLORS[priority]}"></span>
            <span class="group-name">{PRIORITY_LABELS[priority]}</span>
            <span class="group-count">{cards.length}</span>
          </button>

          {#if !collapsedGroups.has(priority)}
            <div class="card-rows">
              {#each cards as card (card.id)}
                <button
                  type="button"
                  class="card-row"
                  onclick={() => onOpenCard(card.id)}
                >
                  <span class="row-dot" style="background: {PRIORITY_COLORS[card.priority]}"></span>
                  <span class="row-title">{card.title}</span>
                  <span class="row-col">{getColumnName(card)}</span>
                  {#if card.due_date}
                    <span class="row-due" class:overdue={isDue(card.due_date)}>{formatDue(card.due_date)}</span>
                  {/if}
                  <span class="row-project">{getProjectName(card)}</span>
                  <span class="row-id">#{card.id.slice(-4)}</span>
                </button>
              {/each}
            </div>
          {/if}
        </div>
      {/if}
    {/each}
  {/if}
</div>

<style>
  .my-tasks {
    flex: 1;
    overflow-y: auto;
    padding: 1.5rem 1.75rem;
    max-width: 900px;
    width: 100%;
    margin: 0 auto;
  }
  .tasks-header {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    margin-bottom: 1.25rem;
  }
  .tasks-header h2 { margin: 0; font-size: 1.125rem; color: #e8eaf0; font-weight: 600; }
  .total-badge {
    background: rgba(255,255,255,0.06);
    color: rgba(255,255,255,0.4);
    font-size: 0.72rem;
    padding: 0.1rem 0.5rem;
    border-radius: 8px;
    font-family: 'JetBrains Mono', monospace;
  }
  .empty-state {
    display: flex; align-items: center; justify-content: center;
    color: rgba(255,255,255,0.2); font-size: 0.875rem; padding: 3rem 0;
  }
  .priority-group { margin-bottom: 0.125rem; }
  .group-header {
    display: flex; align-items: center; gap: 0.4rem;
    width: 100%; background: transparent; border: none;
    color: rgba(255,255,255,0.3); cursor: pointer;
    padding: 0.4rem 0.5rem; border-radius: 5px;
    font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.08em;
    font-weight: 600; transition: background 150ms ease;
  }
  .group-header:hover { background: rgba(255,255,255,0.03); color: rgba(255,255,255,0.5); }
  .group-chevron {
    font-size: 0.75rem; transition: transform 150ms ease; display: inline-block; line-height: 1;
  }
  .group-chevron:not(.collapsed) { transform: rotate(90deg); }
  .priority-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
  .group-name { flex: 1; text-align: left; }
  .group-count {
    background: rgba(255,255,255,0.05); padding: 0.05rem 0.4rem; border-radius: 6px;
    font-size: 0.6rem; color: rgba(255,255,255,0.3);
  }
  .card-rows { display: flex; flex-direction: column; }
  .card-row {
    display: flex; align-items: center; gap: 0.625rem;
    padding: 0.45rem 0.5rem 0.45rem 1.5rem;
    width: 100%;
    background: transparent;
    border: none;
    border-radius: 5px; cursor: pointer;
    transition: background 80ms ease;
    border-bottom: 1px solid rgba(255,255,255,0.025);
    text-align: left;
    font: inherit;
  }
  .card-row:hover { background: rgba(255,255,255,0.04); }
  .row-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
  .row-title {
    flex: 1; font-size: 0.82rem; color: #c0c8d4;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    min-width: 0;
  }
  .row-col {
    font-size: 0.7rem; color: rgba(255,255,255,0.25);
    white-space: nowrap; flex-shrink: 0;
  }
  .row-due {
    font-size: 0.68rem; color: rgba(255,200,80,0.6);
    font-family: 'JetBrains Mono', monospace; flex-shrink: 0; white-space: nowrap;
  }
  .row-due.overdue { color: rgba(255,80,80,0.7); }
  .row-project {
    font-size: 0.68rem; color: rgba(255,255,255,0.18); flex-shrink: 0;
    white-space: nowrap; max-width: 120px; overflow: hidden; text-overflow: ellipsis;
  }
  .row-id {
    font-size: 0.62rem; color: rgba(255,255,255,0.15);
    font-family: 'JetBrains Mono', monospace; flex-shrink: 0;
  }
</style>
